import { useEffect, useRef, useState } from 'react';

/** How far back arrivals count toward the measured writing speed. */
const LOOK_BACK_MS = 2000;

/**
 * How many characters the reveal deliberately stays behind what has arrived.
 * This is the shock absorber: during the silence between two batches there is
 * still text in hand to keep the movement going, and a batch landing tops the
 * buffer back up instead of appearing at once.
 */
const TARGET_BUFFER_CHARS = 15;

/** How long working away a surplus above the buffer may take. */
const RECOVER_MS = 500;

/**
 * The slowest the reveal ever moves while it still holds text. Without it a
 * short reply, or the last buffered characters of a long one, would wait for
 * the finished message instead of completing on screen.
 */
const FLOOR_CHARS_PER_MS = 0.03;

/**
 * How often the visible text may change. Every frame reads as flicker; a dozen
 * times a second, snapped to word ends, reads as words being put down.
 */
const UPDATE_EVERY_MS = 80;

interface Arrival {
  at: number;
  length: number;
}

/**
 * The written text, revealed at the pace it is being written.
 *
 * Pieces arrive in batches — the server sends what gathered over ~100ms — which
 * reads as text landing in lumps. This paces the reveal by measurement instead:
 * how fast has text been arriving lately, that is how fast it is shown, from a
 * small deliberate distance behind so the gaps between batches never show. A
 * quick model reads quick, a deliberate one reads deliberate, and there is no
 * typing speed to tune.
 */
export function useSmoothText(target: string): string {
  const [shown, setShown] = useState(0);
  /** How much is revealed, fractional so slow frames still make progress. */
  const revealed = useRef(0);
  const lastCommit = useRef(0);
  const arrivals = useRef<Arrival[]>([]);

  useEffect(() => {
    const now = performance.now();
    arrivals.current.push({ at: now, length: target.length });
    while (arrivals.current.length > 1 && arrivals.current[0].at < now - LOOK_BACK_MS) {
      arrivals.current.shift();
    }

    if (revealed.current >= target.length) {
      return;
    }

    let frame = 0;
    let last = now;

    const step = (timestamp: number) => {
      const elapsed = timestamp - last;
      last = timestamp;

      const oldest = arrivals.current[0];
      const newest = arrivals.current[arrivals.current.length - 1];
      const span = newest.at - oldest.at;
      const arrivalRate = span > 0 ? (newest.length - oldest.length) / span : 0;

      const backlog = target.length - revealed.current;
      const surplus = Math.max(0, backlog - TARGET_BUFFER_CHARS);
      const paced = arrivalRate * elapsed + (surplus * elapsed) / RECOVER_MS;
      const reveal = Math.max(FLOOR_CHARS_PER_MS * elapsed, paced);

      revealed.current = Math.min(target.length, revealed.current + reveal);

      /*
       * The pace runs every frame; the screen changes on a slower beat, cut at
       * the last finished word so words land whole. A word longer than what
       * has gathered — or the very end — goes out as it stands.
       */
      if (timestamp - lastCommit.current >= UPDATE_EVERY_MS) {
        lastCommit.current = timestamp;
        const caughtUp = revealed.current >= target.length;
        const upTo = Math.floor(revealed.current);
        const wordEnd = target.lastIndexOf(' ', upTo);
        const next = caughtUp || wordEnd <= 0 ? upTo : wordEnd;
        /* What is on screen only ever grows. */
        setShown((previous) => Math.max(previous, next));
      }

      if (revealed.current < target.length) {
        frame = requestAnimationFrame(step);
      } else {
        setShown(target.length);
      }
    };

    frame = requestAnimationFrame(step);
    return () => cancelAnimationFrame(frame);
  }, [target]);

  return target.slice(0, shown);
}
